import { forwardRef, useRef, useEffect } from 'react';
import { motion } from 'framer-motion';
import { cn } from '@/utils/cn';
import { easings } from '@/utils/animations';

interface OTPInputProps {
  length?: number;
  value: string;
  onChange: (value: string) => void;
  error?: string;
  className?: string;
  disabled?: boolean;
}

const OTPInput = forwardRef<HTMLDivElement, OTPInputProps>(
  ({ length = 4, value, onChange, error, className, disabled = false }, ref) => {
    const inputRefs = useRef<(HTMLInputElement | null)[]>([]);
    const hasError = !!error;

    // تبدیل اعداد انگلیسی به فارسی
    const toPersianDigits = (str: string): string => {
      const persianDigits = ['۰', '۱', '۲', '۳', '۴', '۵', '۶', '۷', '۸', '۹'];
      return str.replace(/\d/g, (digit) => persianDigits[parseInt(digit)]);
    };

    // تبدیل اعداد فارسی به انگلیسی
    const toEnglishDigits = (str: string): string => {
      const persianDigits = ['۰', '۱', '۲', '۳', '۴', '۵', '۶', '۷', '۸', '۹'];
      let result = str;
      persianDigits.forEach((persianDigit, index) => {
        result = result.replace(new RegExp(persianDigit, 'g'), index.toString());
      });
      return result;
    };

    useEffect(() => {
      // Focus first input on mount (rightmost in RTL)
      if (inputRefs.current[0] && !disabled) {
        inputRefs.current[0].focus();
      }
    }, [disabled]);

    const handleChange = (visualIndex: number, inputValue: string) => {
      // تبدیل به انگلیسی و فقط اعداد را قبول کن
      const englishValue = toEnglishDigits(inputValue);
      const digit = englishValue.replace(/\D/g, '').slice(-1);
      
      // In RTL, convert visual index to logical index
      // Visual index 0 (rightmost) = logical index 0
      // Visual index 1 = logical index 1, etc.
      const logicalIndex = visualIndex;
      
      const newValue = value.split('');
      newValue[logicalIndex] = digit;
      
      // Pad with empty strings if needed
      while (newValue.length < length) {
        newValue.push('');
      }
      
      onChange(newValue.join(''));

      // Auto-focus next input (move to next visual position, which is left)
      if (digit && visualIndex < length - 1) {
        inputRefs.current[visualIndex + 1]?.focus();
      }
    };

    const handleKeyDown = (visualIndex: number, e: React.KeyboardEvent<HTMLInputElement>) => {
      const logicalIndex = visualIndex;
      
      if (e.key === 'Backspace') {
        if (!value[logicalIndex] && visualIndex > 0) {
          // If current input is empty, focus previous (visually right) and clear it
          inputRefs.current[visualIndex - 1]?.focus();
          const newValue = value.split('');
          newValue[visualIndex - 1] = '';
          onChange(newValue.join(''));
        } else {
          // Clear current input
          const newValue = value.split('');
          newValue[logicalIndex] = '';
          onChange(newValue.join(''));
        }
      } else if (e.key === 'ArrowLeft' && visualIndex < length - 1) {
        // Left arrow goes to next input (visually left)
        inputRefs.current[visualIndex + 1]?.focus();
      } else if (e.key === 'ArrowRight' && visualIndex > 0) {
        // Right arrow goes to previous input (visually right)
        inputRefs.current[visualIndex - 1]?.focus();
      }
    };

    const handlePaste = (e: React.ClipboardEvent) => {
      e.preventDefault();
      const englishData = toEnglishDigits(e.clipboardData.getData('text'));
      const pastedData = englishData.replace(/\D/g, '').slice(0, length);
      onChange(pastedData.padEnd(length, ''));
      
      // Focus the next empty input or the last input
      const nextIndex = Math.min(pastedData.length, length - 1);
      inputRefs.current[nextIndex]?.focus();
    };

    return (
      <motion.div 
        ref={ref}
        className={cn('w-full space-y-4', className)}
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.4, ease: easings.easeOut }}
      >
        {/* OTP Input Fields */}
        <div className="flex justify-center gap-3 flex-row-reverse">
          {Array.from({ length }, (_, visualIndex) => {
            const logicalIndex = visualIndex;
            return (
            <motion.input
              key={visualIndex}
              ref={(el) => {
                inputRefs.current[visualIndex] = el;
              }}
              type="tel"
              inputMode="numeric"
              pattern="[0-9]*"
              autoComplete="one-time-code"
              maxLength={1}
              value={toPersianDigits(value[logicalIndex] || '')}
              onChange={(e) => handleChange(visualIndex, e.target.value)}
              onKeyDown={(e) => handleKeyDown(visualIndex, e)}
              onPaste={handlePaste}
              disabled={disabled}
              className={cn(
                // Base styles
                'w-12 h-14 text-center text-xl font-bold rounded-xl border-2 transition-all duration-300',
                'font-persian text-foreground bg-soft-white shadow-md',
                'focus:outline-none focus:ring-4 focus:ring-offset-2',
                'disabled:opacity-50 disabled:cursor-not-allowed',
                
                // Default state - minimalistic focus
                !hasError && [
                  'border-gray-200',
                  'hover:border-gray-300 hover:shadow-lg',
                  'focus:border-gray-400 focus:ring-gray-200' // Subtle neutral focus
                ],
                
                // Error state
                hasError && [
                  'border-red-300 bg-red-50',
                  'hover:border-red-400',
                  'focus:border-red-500 focus:ring-red-200'
                ],
                
                // Filled state
                value[logicalIndex] && !hasError && 'border-primary-400 bg-primary-50'
              )}
              whileFocus={{ scale: 1.05 }}
              transition={{ duration: 0.2 }}
            />
            );
          })}
        </div>

        {/* Error message */}
        {error && (
          <motion.p
            className="text-red-500 text-sm font-persian text-center"
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.3 }}
          >
            {error}
          </motion.p>
        )}
      </motion.div>
    );
  }
);

OTPInput.displayName = 'OTPInput';

export default OTPInput;
